import { usePublishArtifactReviewQueue } from "@/components/artifacts/artifactReviewQueue.ts";
import { isBrowserSignInArtifact } from "@/components/artifacts/artifactUtils.ts";
import {
  selectOmniCards,
  useBrowsedArtifact,
  useSetBrowsedArtifact,
} from "@/components/artifacts/browsedArtifact.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { useCommitArtifactDecisions } from "@/components/messages/useCommitArtifactDecisions.ts";
import { useSubmitForm } from "@/hooks/useArtifacts.tsx";
import {
  buildArtifactDecisionsRequest,
  buildOptimisticDecisionsContent,
  useStagedArtifactDecisions,
} from "@/shared/artifactDecisions.ts";
import { resolveActiveOmniIndex } from "@/shared/omniCarouselPosition.ts";
import { toUrnString } from "@/types/urn.ts";
import {
  countDecidedCards,
  nextReviewArtifacts,
  spentFormUrns,
} from "@/shared/reviewArtifactSet.ts";
import { useCanvasStackTop } from "@/components/threads/useThreadCanvas.tsx";
import { useComposerDraftHandoff } from "@/components/messages/Omnibox/composerDraft.ts";
import {
  clearPendingAttachments,
  seedPendingAttachments,
} from "@/components/messages/Omnibox/attachmentSend.ts";
import type { AttachedFile } from "@/components/messages/Omnibox/types.ts";
import { pendingThreadAttachmentsAtom } from "@/atoms/messagesAtoms.ts";
import { useSetAtom } from "jotai";
import { errorToast } from "@/components/ui/toast.tsx";
import { useEmbedded } from "@/hooks/useEmbedded.tsx";
import {
  type OmniArtifact,
  selectTurnOmniCards,
  threadIsAwaitingUserDecision,
} from "@/shared/selectOmniArtifacts.ts";
import type { ArtifactAction, ArtifactDecisionsReq } from "@/types/artifacts.ts";
import {
  type AskUserFormField,
  formAwaitsAnswer,
  isActionableArtifact,
} from "@/types/artifacts.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NewMessageReq, ThreadEventItem } from "@/types/threadEvents.ts";

/**
 * Whether a form question's answer box would show free text as typed. A
 * multi-select box holds an unbanked pick, not the answer, and a number or date
 * input renders anything else as blank.
 */
/** Shared empty list, so a card with no files keeps a stable prop. */
const NO_FILES: AttachedFile[] = [];

const showsTypedAnswer = (field: AskUserFormField): boolean =>
  field.type !== "multiSelect" &&
  (field.type !== "text" || field.inputType === undefined || field.inputType === "text");

/**
 * The omni-artifact review: the state machine behind the card carousel that
 * takes over the composer when a turn leaves something awaiting the user.
 *
 * It lived inside `MessageList` and coupled to it through nothing but `items`
 * in and the rendered queue out — 300-odd lines of queue state in the middle
 * of a component whose job is rendering a feed. Everything about paging,
 * staging and committing decisions is here; `MessageList` only renders what it
 * returns.
 */
export function useOmniReview({
  thread,
  items,
  readOnly,
  addOptimisticUserMessage,
}: {
  thread: GetThreadResp;
  items: ThreadEventItem[];
  readOnly: boolean;
  addOptimisticUserMessage: (body: NewMessageReq) => {
    clientMessageId: number;
    rollback: () => void;
  };
}) {
  // The omni-overlay surfaces only when the server says the thread is awaiting
  // the user — `activityState`, set by the one server look-back at the turn's
  // end. Keying the overlay off that backend signal instead of a
  // message-derived guess is what BIG-5581 asked for: a turn that ended FINAL
  // with a pending approval reads as waiting, and one that truly completed
  // shows no overlay even if a stale artifact lingers in `items`.
  //
  // Reads the signal directly rather than through the status badge, which
  // answers a different question and cannot say "paused *and* awaiting" —
  // see `threadIsAwaitingUserDecision`.
  const isAwaitingUser = threadIsAwaitingUserDecision(thread);

  /**
   * Every artifact in the current agent turn an omni surface can render, decided
   * or not, and the subset still awaiting the user — the single source of truth
   * fanning out to all omni-artifact surfaces (the overlay carousel, the bottom
   * list, the inline-collapse decision). See `selectTurnOmniCards` for the walk.
   *
   * A held card re-reads itself from the wider list, so a draft the user just
   * sent re-renders as sent rather than freezing at the snapshot it held when it
   * stopped awaiting the user.
   */
  const turnOmniCards = useMemo(() => selectTurnOmniCards(items), [items]);
  const liveOmniArtifacts = useMemo(
    () => turnOmniCards.filter(isActionableArtifact),
    [turnOmniCards],
  );

  /**
   * The cards the review pages through. A card that resolves leaves
   * `liveOmniArtifacts` immediately, but it stays here — showing itself as
   * decided — until every card is decided, so a resolution doesn't renumber the
   * queue or take the pager away from the card the user is on.
   *
   * The review's own decisions no longer resolve cards mid-review (they stage
   * until the commit), but a card can still resolve underneath it: answered on
   * the canvas, on another device, or superseded by a fresh agent run.
   *
   * A review *opens* only on the server's "awaiting the user" signal, the gate
   * that keeps a completed turn from resurfacing a stale artifact (BIG-5581).
   * It does not *close* on that signal, because anything that starts an agent
   * run stops the thread awaiting the user, and closing there would strand the
   * user mid-review with two drafts left and no carousel to reach them
   * (BIG-7171). What ends a review is running out of undecided cards.
   */
  const [omniArtifacts, setOmniArtifacts] = useState<readonly OmniArtifact[]>([]);
  useEffect(() => {
    setOmniArtifacts((previous) =>
      nextReviewArtifacts({
        previous,
        live: liveOmniArtifacts,
        turnCards: turnOmniCards,
        isAwaitingUser,
      }),
    );
  }, [isAwaitingUser, liveOmniArtifacts, turnOmniCards]);

  // Carousel position tracked by artifact id, not index, so the view stays put
  // as the set mutates: a newly drafted artifact appends without moving the
  // user. When the tracked id is gone (or unset), `resolveActiveOmniIndex` falls
  // back to the artifact the canvas is presenting, then to the top of the queue.
  const [activeOmniId, setActiveOmniId] = useState<string | undefined>(undefined);
  const omniKey = useMemo(() => omniArtifacts.map((a) => a.id).join(","), [omniArtifacts]);
  // Escape "throws away" the overlay (data loss accepted). The pending artifacts
  // are still in `items`, so clearing state alone would re-show the biscuit —
  // suppress it while the dismissed key still matches the current surfaced set.
  const [dismissedOmniKey, setDismissedOmniKey] = useState<string | undefined>(undefined);
  const isOmniDismissed = omniKey !== "" && dismissedOmniKey === omniKey;

  // Aligns the carousel's default with the card the canvas is already presenting
  // (see `resolveActiveOmniIndex`). The context handle is read only to tell
  // whether this surface has a canvas at all: no provider means mobile /
  // gate-off, where there's nothing to align with and no reason to subscribe to
  // the canvas query.
  const canvas = useCanvasActionContext();
  const hasCanvas = !!canvas;
  const presentedUrn = useCanvasStackTop(thread.id, { enabled: hasCanvas });

  // Opening an artifact from the feed points the card at it. A card already in
  // the review is reached by selecting it, not by overriding: that keeps the
  // pager, the staged notes and the decided count intact, and it beats the
  // `activeOmniId` a previous arrow press left behind — which would otherwise
  // pin the carousel while the canvas moved on without it.
  const browsedArtifact = useBrowsedArtifact(thread.id);
  const setBrowsedArtifact = useSetBrowsedArtifact();
  /**
   * Give the surface back — clears whatever this thread is showing.
   *
   * **Every caller must be synchronous.** Clearing "whatever is there now" is
   * only right while now is still now: a release landing after a network
   * round-trip can take down a card the user opened in the meantime. That holds
   * here because a browsed card never submits through the review (see
   * `submitBrowsedForm`), so no release is ever queued behind a request — each
   * caller is a user action or an effect reacting to state already settled.
   * Anything added later that releases from a callback has to carry the card's
   * identity with it rather than use this.
   */
  const releaseBrowsed = useCallback(
    () => setBrowsedArtifact((prev) => (prev?.threadId === thread.id ? undefined : prev)),
    [setBrowsedArtifact, thread.id],
  );

  /**
   * What this surface shows: the review, or an artifact the user opened from an
   * earlier turn standing in front of it (see {@link selectOmniCards}).
   *
   * A browsed card takes the surface over rather than joining the review, since
   * one the user opened to look at is not one they enlisted for decision, and
   * letting it into the queue would commit it with the review and count it as
   * decided. The review is only hidden, never discarded — it is still
   * derived from `items` — so releasing the card brings it back as it was.
   */
  // `cards` is what this surface renders; `omniArtifacts` stays what the review
  // commits. Keeping the two apart is the whole point: a browsed card can be on
  // screen without ever being something the review sends.
  const { cards, isBrowsing, selectId } = useMemo(
    () => selectOmniCards({ review: omniArtifacts, browsed: browsedArtifact }),
    [omniArtifacts, browsedArtifact],
  );
  useEffect(() => {
    if (selectId === undefined) return;
    setActiveOmniId(selectId);
    releaseBrowsed();
  }, [selectId, releaseBrowsed]);

  /**
   * A browsed form's answers, and the mutation that sends them.
   *
   * Deliberately not the review's staging map or its commit. The review batches
   * decisions so a queue costs one agent run; a browsed card is alone, so it has
   * nothing to batch with and no reason to borrow machinery whose lifetime is
   * the turn. Sharing the two is what made a browsed submission able to clobber
   * notes staged on the review, and what put a network round-trip between
   * "settle this card" and the state it settled against.
   *
   * This is the same endpoint the canvas uses for the same form
   * (`AskUserForm` → `useSubmitForm`), so both surfaces answer it the same way.
   */
  const [browsedFormValues, setBrowsedFormValues] = useState<Record<string, string>>({});
  const submitBrowsedFormMutation = useSubmitForm({
    threadId: thread.id,
    messageId: browsedArtifact?.messageId ?? 0,
  });
  // Re-init when the browsed card changes, so answers never carry between forms.
  const browsedFormId = isBrowsing ? browsedArtifact?.id : undefined;
  const browsedFormIdRef = useRef(browsedFormId);
  if (browsedFormIdRef.current !== browsedFormId) {
    browsedFormIdRef.current = browsedFormId;
    setBrowsedFormValues({});
  }

  /**
   * Answer a browsed form and hand the surface back.
   *
   * Released here rather than when the request returns: the user has answered,
   * so the card's job is done, and a release waiting on the network is the late
   * write this whole path exists to avoid. A failure leaves the artifact
   * unanswered server-side, so the form is still there to reopen, and the toast
   * says so.
   */
  const submitBrowsedForm = useCallback(
    (values: Record<string, string>) => {
      const artifact = browsedArtifact;
      if (!artifact) return;
      releaseBrowsed();
      setBrowsedFormValues({});
      submitBrowsedFormMutation.mutate(
        { artifactId: artifact.id, values },
        {
          onError: (e) =>
            errorToast(e instanceof Error ? e.message : "Couldn't submit your answer."),
        },
      );
    },
    [browsedArtifact, releaseBrowsed, submitBrowsedFormMutation],
  );

  // A turn that ends with something to decide reclaims the surface: whatever the
  // user was looking at, the agent is now waiting on them, and that outranks it.
  // Edge-triggered, so it hands the surface back once per review rather than
  // pinning it — the user can open an older artifact again while the same review
  // stands. A turn that ends with nothing to decide leaves the browsed card be.
  const hasFreshReview = isAwaitingUser && liveOmniArtifacts.length > 0;
  const hadFreshReviewRef = useRef(hasFreshReview);
  useEffect(() => {
    const had = hadFreshReviewRef.current;
    hadFreshReviewRef.current = hasFreshReview;
    if (!had && hasFreshReview) releaseBrowsed();
  }, [hasFreshReview, releaseBrowsed]);

  const activeOmniIndex = isBrowsing
    ? 0
    : resolveActiveOmniIndex(omniArtifacts, activeOmniId, presentedUrn);
  // Dismissal suppresses the review, not a card the user has just asked to see.
  const biscuitArtifact: OmniArtifact | undefined =
    isOmniDismissed && !isBrowsing ? undefined : cards[activeOmniIndex];
  // Steps from the resolved index rather than from `activeOmniId`, so the first
  // arrow press moves off the canvas-derived default instead of re-selecting it.
  const stepOmniArtifact = useCallback(
    (delta: number) => {
      const next = cards[activeOmniIndex + delta];
      if (next) setActiveOmniId(next.id);
    },
    [cards, activeOmniIndex],
  );

  // --- Review queue (≥2 surfaced artifacts) ---------------------------------
  // Every decision stages while a queue is live — actions included. Three drafts
  // reviewed together are one thing the user did, and they submit as one request
  // and one agent run rather than three that start replying while the user is
  // still reading draft two (BIG-7346). A lone artifact has nothing to batch
  // with and keeps firing on click.
  const isQueue = cards.length > 1;
  const {
    decisions,
    stageAction,
    stageNote,
    stageFormValues,
    reset: resetDecisions,
  } = useStagedArtifactDecisions();
  const { isWebView } = useEmbedded();
  const commitDecisions = useCommitArtifactDecisions(thread.id);
  const submittedRef = useRef(false);
  const setPendingAttachments = useSetAtom(pendingThreadAttachmentsAtom);

  /**
   * Files attached on a form card, keyed by artifact id so they follow the card
   * as the user pages the queue (BIG-9261). Held beside the staged decisions
   * rather than in them: they are bytes to upload, not part of the wire
   * decision, and they commit with the batch as its one message's attachments.
   */
  const [formFiles, setFormFiles] = useState<ReadonlyMap<string, AttachedFile[]>>(() => new Map());
  const decidedCount = useMemo(
    () => countDecidedCards({ review: cards, live: liveOmniArtifacts, decisions }),
    [cards, liveOmniArtifacts, decisions],
  );
  const isLastOmni = activeOmniIndex >= cards.length - 1;

  const submitReview = useCallback(
    (req: ArtifactDecisionsReq, optimisticText: string) => {
      if (submittedRef.current) return;
      // Only ever the review's own cards: a browsed card submits on its own (see
      // `submitBrowsedForm`), so nothing here can run against state the user has
      // since moved on from.
      const review = omniArtifacts;
      // Only the files of forms this commit answers: a card the user attached to
      // and then skipped stays pending, and so do its files.
      const answeredFormIds = new Set(
        req.decisions.filter((d) => d.kind === "form").map((d) => d.artifactId),
      );
      const attachedFiles = [...formFiles]
        .filter(([artifactId]) => answeredFormIds.has(artifactId))
        .flatMap(([, files]) => files);
      const finish = () => {
        resetDecisions();
        setFormFiles(new Map());
        setActiveOmniId(undefined);
        // Take the answered forms off the canvas — see `spentFormUrns`.
        for (const urn of spentFormUrns(review, req.decisions)) canvas?.dropContent(urn);
      };
      // Walked through and skipped everything — dismiss without an agent run.
      // The cards are still pending, so without the dismissal the review would
      // reopen on them at once.
      if (req.decisions.length === 0) {
        finish();
        setDismissedOmniKey(omniKey);
        return;
      }
      submittedRef.current = true;
      // Optimistic write so the committed message behaves like a normal send:
      // straight into the feed on an idle thread, queued pill on a running one
      // (the placeholder carries `suppressQueuedPill`; the server maps the real
      // message back via `tempClientId`).
      const { clientMessageId, rollback: rollbackMessage } = addOptimisticUserMessage({
        type: "userRequest",
        text: optimisticText,
      });
      // The same uploading chips a composer send shows, under the same message.
      if (attachedFiles.length > 0) {
        seedPendingAttachments(setPendingAttachments, thread.id, clientMessageId, attachedFiles);
      }
      const rollback = () => {
        rollbackMessage();
        if (attachedFiles.length > 0) clearPendingAttachments(setPendingAttachments, thread.id);
      };
      commitDecisions.mutate(
        { ...req, tempClientId: clientMessageId, attachedFiles },
        {
          onSuccess: (resp) => {
            // When nothing applied (every decision failed), the server wrote no
            // userRequest message — so the optimistic placeholder has nothing to
            // reconcile against and would linger. Roll it back.
            if (!resp.data.workflowEnqueued) {
              rollback();
            }
            // The endpoint returns 200 with per-item results even on a partial
            // failure (e.g. a form answer that fails server-side validation). The
            // failed artifacts stay pending and re-surface, so flag it rather than
            // letting the queue close as if everything went through.
            const failed = resp.data.results.filter((r) => r.status === "failed");
            finish();
            if (failed.length > 0) {
              errorToast(failed[0].error ?? "Some items couldn't be submitted.");
            }
          },
          onError: (e) => {
            // Roll back the optimistic placeholder + running state; the staged
            // decisions stay put for a retry.
            rollback();
            errorToast(e instanceof Error ? e.message : "Couldn't submit your reviews.");
          },
          onSettled: () => {
            submittedRef.current = false;
          },
        },
      );
    },
    [
      resetDecisions,
      commitDecisions,
      addOptimisticUserMessage,
      omniArtifacts,
      omniKey,
      canvas,
      formFiles,
      setPendingAttachments,
      thread.id,
    ],
  );

  // End the review with what was staged along the way, and nothing else. Every
  // way out of the last card lands here — Enter / → past it, and the Skip that
  // replaces its Next — so a card the user didn't decide stays pending.
  const skipRemaining = useCallback(() => {
    submitReview(
      buildArtifactDecisionsRequest(omniArtifacts, decisions),
      buildOptimisticDecisionsContent(omniArtifacts, decisions) || "Submitted your review.",
    );
  }, [submitReview, omniArtifacts, decisions]);

  // Forward navigation (Enter / → / Tab / Next) advances through the queue and
  // commits once you advance past the last card. Back navigation never commits.
  const advanceOrCommit = useCallback(() => {
    if (isLastOmni) skipRemaining();
    else stepOmniArtifact(1);
  }, [isLastOmni, skipRemaining, stepOmniArtifact]);

  /**
   * Choose this card's action, then move on: step to the next card, or — on the
   * last one — finish the review.
   *
   * Deciding the last card commits the review: this choice plus everything
   * staged along the way. Cards the user walked past without deciding stay
   * pending — answering one card is not answering the ones behind it, so it
   * must not send drafts or accept invites on the user's behalf.
   *
   * Addressed by artifact id rather than by the active index, because the canvas
   * stages for the card it is showing and the carousel is not guaranteed to be
   * sitting on it.
   */
  const stageActionAndAdvance = useCallback(
    (artifactId: string, action: ArtifactAction) => {
      if (commitDecisions.isPending) return;
      const index = omniArtifacts.findIndex((a) => a.id === artifactId);
      if (index < 0) return;
      // Stage first even on the commit path: a failed commit keeps the staged
      // decisions for a retry, and this one has to be among them or the retry
      // sends every decision except the one the user just made.
      stageAction(artifactId, action);
      if (index === omniArtifacts.length - 1) {
        // Build the map fresh — the setState above hasn't landed in `decisions`.
        const staged = new Map(decisions).set(artifactId, { kind: "action" as const, action });
        submitReview(
          buildArtifactDecisionsRequest(omniArtifacts, staged),
          buildOptimisticDecisionsContent(omniArtifacts, staged) || "Submitted your review.",
        );
        return;
      }
      const next = omniArtifacts[index + 1];
      if (next) setActiveOmniId(next.id);
    },
    [commitDecisions.isPending, omniArtifacts, stageAction, decisions, submitReview],
  );

  // The canvas presents these artifacts too and resolves them by URN, with no
  // way to know they're mid-review — publish the queue so its header can page
  // through the review and end it, and so deciding a card there moves the
  // carousel on rather than dropping the user back to the calendar.
  //
  // Published only while the biscuit itself is on screen to own the review: it
  // holds the staged notes, and it's what carries the canvas to the artifact a
  // canvas-side arrow selects. Browsing takes the review off screen, so it stops
  // publishing too — a canvas-side arrow should not page a review the user
  // cannot see. `isQueue` covers that, being false whenever a browsed card has
  // the surface.
  const isReviewQueueLive =
    isQueue && !isOmniDismissed && !readOnly && !isWebView && thread.status !== "completed";

  // When the card surface opens, whatever the user had typed in the composer
  // becomes the answer on the first card shown (BIG-8541): the first question
  // of a form, or the note on a card in a queue. A lone heavy card needs none of
  // this — its "Ask a follow up" row is an Omnibox that reads the same draft.
  //
  // Staged during render, as the open edge is seen, rather than in an effect:
  // the form card seeds its inputs from the staged answers when it mounts, and
  // it mounts in this same commit. Only the read happens here; emptying the
  // composer is a write to a store other components read, so it waits for the
  // effect below. A decision already staged on that card wins over the draft.
  //
  // Keyed by thread and card set rather than by "is a card open": the thread
  // view stays mounted when the user moves between threads, so going from one
  // thread's open card straight to another's never passes through "closed".
  const draftHandoff = useComposerDraftHandoff(thread.id);
  // The card must come from this thread's own turn. The review's card list is
  // state that catches up a render after the thread changes, so for one render
  // it can still be the previous thread's cards.
  const isSurfaceOpen =
    biscuitArtifact !== undefined &&
    turnOmniCards.some((card) => card.id === biscuitArtifact.id) &&
    !isBrowsing &&
    !readOnly &&
    !isWebView &&
    thread.status !== "completed";
  const surfaceKey = isSurfaceOpen ? `${thread.id}:${omniKey}` : undefined;
  const [seenSurfaceKey, setSeenSurfaceKey] = useState<string | undefined>(undefined);
  const [draftHandedOff, setDraftHandedOff] = useState(false);
  if (surfaceKey !== seenSurfaceKey) {
    setSeenSurfaceKey(surfaceKey);
    const text = surfaceKey !== undefined ? draftHandoff.peek() : "";
    if (biscuitArtifact && text.trim() && !decisions.has(biscuitArtifact.id)) {
      const isForm = biscuitArtifact.artifactType === "userResponse";
      const firstField =
        isForm && formAwaitsAnswer(biscuitArtifact.form)
          ? biscuitArtifact.form.fields[0]
          : undefined;
      if (firstField) {
        // A question that cannot show the text as typed keeps it in the
        // composer, rather than taking an answer the user would never see.
        if (showsTypedAnswer(firstField)) {
          stageFormValues(biscuitArtifact.id, { [firstField.id]: text });
          setDraftHandedOff(true);
        }
      } else if (isQueue) {
        stageNote(biscuitArtifact.id, text);
        setDraftHandedOff(true);
      }
    }
  }
  useEffect(() => {
    if (!draftHandedOff) return;
    draftHandoff.clear();
    setDraftHandedOff(false);
  }, [draftHandedOff, draftHandoff]);

  // Only the actions: a note or a form answer has no row on another surface to
  // mark, and the biscuit reads those from `decisions` directly.
  const stagedActions = useMemo(() => {
    const out = new Map<string, ArtifactAction>();
    for (const [artifactId, decision] of decisions) {
      if (decision.kind === "action") out.set(artifactId, decision.action);
    }
    return out;
  }, [decisions]);
  const reviewQueue = useMemo(
    () => ({
      stageAction: stageActionAndAdvance,
      staged: stagedActions,
      setActive: setActiveOmniId,
      commit: skipRemaining,
    }),
    [stageActionAndAdvance, stagedActions, skipRemaining],
  );
  usePublishArtifactReviewQueue(
    isReviewQueueLive ? omniArtifacts.map((a) => a.id) : undefined,
    reviewQueue,
  );

  // A form card hands back its answers when the user advances past its last
  // field: stage + step to the next artifact, or commit if it's the last card
  // (build the map fresh — the just-typed values aren't in `decisions` yet).
  const stageFormAndAdvance = useCallback(
    (values: Record<string, string>) => {
      if (!biscuitArtifact) return;
      // A browsed form answers on its own — see `submitBrowsedForm`.
      if (isBrowsing) {
        submitBrowsedForm(values);
        return;
      }
      const id = biscuitArtifact.id;
      if (isLastOmni) {
        const answered = new Map(decisions).set(id, { kind: "form" as const, values });
        submitReview(
          buildArtifactDecisionsRequest(omniArtifacts, answered),
          buildOptimisticDecisionsContent(omniArtifacts, answered) || "Submitted your review.",
        );
        return;
      }
      stageFormValues(id, values);
      stepOmniArtifact(1);
    },
    [
      biscuitArtifact,
      isBrowsing,
      submitBrowsedForm,
      isLastOmni,
      submitReview,
      omniArtifacts,
      decisions,
      stageFormValues,
      stepOmniArtifact,
    ],
  );

  // Live-stage form answers as the user edits (so navigating away preserves them).
  // A browsed form keeps its own: the review's map is for cards being batched
  // into one run, which this is not.
  const stageActiveFormValues = useCallback(
    (values: Record<string, string>) => {
      if (isBrowsing) {
        setBrowsedFormValues(values);
        return;
      }
      if (biscuitArtifact) stageFormValues(biscuitArtifact.id, values);
    },
    [isBrowsing, biscuitArtifact, stageFormValues],
  );

  // The note input is controlled per-artifact: typing stages live (so it's
  // stored with the right artifact and restored on navigation); Enter / boundary
  // arrows navigate (never commit).
  const activeNoteDecision = biscuitArtifact ? decisions.get(biscuitArtifact.id) : undefined;
  const activeNoteText = activeNoteDecision?.kind === "note" ? activeNoteDecision.text : "";
  const activeFormValues = isBrowsing
    ? browsedFormValues
    : activeNoteDecision?.kind === "form"
      ? activeNoteDecision.values
      : undefined;
  // The active form card's attachments. A browsed form has none: it answers
  // through the single-form endpoint, which takes no files, so its card offers
  // no attach control at all rather than one whose files would be dropped.
  const activeFormId =
    biscuitArtifact?.artifactType === "userResponse" && !isBrowsing
      ? biscuitArtifact.id
      : undefined;
  const activeFormFiles = activeFormId ? (formFiles.get(activeFormId) ?? NO_FILES) : undefined;
  const setActiveFormFiles = useCallback(
    (update: AttachedFile[] | ((prev: AttachedFile[]) => AttachedFile[])) => {
      if (!activeFormId) return;
      setFormFiles((prev) => {
        const current = prev.get(activeFormId) ?? NO_FILES;
        const next = typeof update === "function" ? update(current) : update;
        return new Map(prev).set(activeFormId, next);
      });
    },
    [activeFormId],
  );

  const handleNoteKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (commitDecisions.isPending) return;
      // Tab / Shift+Tab step the queue (and keep focus in the note box).
      if (e.key === "Tab") {
        e.preventDefault();
        if (e.shiftKey) stepOmniArtifact(-1);
        else advanceOrCommit();
        return;
      }
      const el = e.currentTarget;
      const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
      const atEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
      if (e.key === "Enter" || (e.key === "ArrowRight" && atEnd)) {
        e.preventDefault();
        advanceOrCommit();
      } else if (e.key === "ArrowLeft" && atStart) {
        e.preventDefault();
        stepOmniArtifact(-1);
      }
    },
    [commitDecisions.isPending, advanceOrCommit, stepOmniArtifact],
  );

  // Escape throws the overlay away (data loss accepted). ArtifactBiscuit fires
  // this from a capture-phase handler so it can't bubble to back-navigation.
  //
  // On a browsed card it puts back what that card was covering instead. Nothing
  // is thrown away there — the review is still live behind it, staged notes
  // included, so resetting decisions here would discard work the user did before
  // they went looking at something else.
  //
  // The canvas comes back with it. These two surfaces are meant to be describing
  // the same artifact — that is the whole point of following a click over here —
  // so leaving the released card on the canvas beside the restored review breaks
  // the pairing at exactly the moment it should be restored.
  //
  // Either way the panel itself collapses. Emptying it alone left it standing
  // open on the resting calendar, so dismissing the card did not read as
  // closing anything on the right. The stack is still kept in step below, so
  // reopening the panel shows what belongs there.
  const dismissOverlay = useCallback(() => {
    canvas?.close();
    if (isBrowsing && browsedArtifact) {
      releaseBrowsed();
      if (!canvas) return;
      // Only when the canvas is still on the card being released. A drill-down
      // from it, or a collapsed panel, is somewhere the user navigated to
      // deliberately and must not be yanked — the rule `useCanvasFollowsCarousel`
      // already applies to the carousel's own moves.
      const browsedUrn = toUrnString({
        type: browsedArtifact.artifactType,
        id: browsedArtifact.id,
      });
      if (canvas.getSnapshot()?.canvasItem !== browsedUrn) return;
      // Resolved without the presented URN: that names the card being released,
      // so it has no say in which one the review comes back to.
      const restored =
        omniArtifacts[resolveActiveOmniIndex(omniArtifacts, activeOmniId, undefined)];
      if (restored && !isBrowserSignInArtifact(restored)) {
        canvas.resetTo(toUrnString({ type: restored.artifactType, id: restored.id }));
      } else {
        // Nothing behind it, so there is no card to match — take the released
        // artifact down rather than leave it as the answer to a question the
        // omnibox is no longer asking.
        canvas.dropContent(browsedUrn);
      }
      return;
    }
    resetDecisions();
    setFormFiles(new Map());
    setActiveOmniId(undefined);
    setDismissedOmniKey(omniKey);
    // Escape on the review is a throw-away, so the canvas goes with it rather
    // than being left presenting a card from a review that is no longer on
    // screen. Unguarded, unlike the browsed-card release above: that one puts
    // something back and has to respect where the user navigated to, while this
    // one is the user clearing the whole thing off.
    canvas?.clear();
  }, [
    isBrowsing,
    browsedArtifact,
    releaseBrowsed,
    canvas,
    omniArtifacts,
    activeOmniId,
    resetDecisions,
    omniKey,
  ]);

  // When embedded in the native WebView, the native ChatInput renders the
  // biscuit as part of its own card chrome (see native's `ArtifactBiscuitSection`).
  // Push the active artifact across the bridge whenever it changes — including
  // the transition to `undefined` so native clears when the artifact resolves.
  // Forms are web-only (neither the stepped wizard nor the form biscuit has a
  // native counterpart yet) and render inline natively, so they're never
  // bridged — native sees `null`. Gated on `isHeavy` rather than "is this form
  // still unanswered": an answered form is just as much a form, and native's
  // biscuit section can only render a heavy artifact.
  const bridgedArtifact = biscuitArtifact?.isHeavy ? biscuitArtifact : null;
  useEffect(() => {
    if (!window.isWebView) return;
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: "biscuit", artifact: bridgedArtifact }),
    );
  }, [bridgedArtifact]);

  return {
    /** The card the review is showing, or undefined when there is none. */
    biscuitArtifact,
    /** More than one card in play, which is what turns the pager on. */
    isQueue,
    activeOmniIndex,
    /** The cards on this surface — the review, or a browsed artifact alone. */
    cards,
    decidedCount,
    commitDecisions,
    stepOmniArtifact,
    advanceOrCommit,
    stageActionAndAdvance,
    skipRemaining,
    stageNote,
    stageActiveFormValues,
    stageFormAndAdvance,
    handleNoteKeyDown,
    dismissOverlay,
    activeNoteText,
    activeFormValues,
    /** Files attached on the active form card; undefined where it takes none. */
    activeFormFiles,
    setActiveFormFiles,
    /** Staged notes and form answers, keyed by artifact id. */
    decisions,
  };
}
